import { useEffect, DependencyList, EffectCallback } from 'react'
import { useRequestUserConfigContext } from './request'

/**
 * Custom hook that wraps useEffect to ensure the effect only runs after session is initialized.
 * This prevents race conditions where multiple API calls might create different sessions.
 *
 * @param effect - The effect callback to run (same as useEffect)
 * @param deps - Dependencies array (same as useEffect)
 *
 * @example
 * useSessionEffect(() => {
 *   // This will only run after sessionId is available
 *   fetchUserDetails()
 * }, [otherDependency])
 */
export const useSessionEffect = (effect: EffectCallback, deps: DependencyList = []) => {
    const user = useRequestUserConfigContext()
    const sessionId = user.session?.id

    useEffect(() => {
        if (!sessionId) {
            // Session not ready yet, skip effect
            return
        }

        // Session is ready, run the effect
        return effect()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, ...deps])
}

/**
 * Hook that returns the current session ID and a boolean indicating if session is ready.
 * Useful when you need to conditionally render or make decisions based on session availability.
 *
 * @example
 * const { sessionId, isSessionReady } = useSession()
 * if (!isSessionReady) return <Loading />
 */
export const useSession = () => {
    const user = useRequestUserConfigContext()
    const sessionId = user.session?.id

    return {
        sessionId,
        isSessionReady: !!sessionId,
    }
}
